<template>
  <GeneralListComponent title="Transporter Tariff">
    <template #list>
      <v-row>
        <v-col cols="12">
          <!-- Tabs -->
          <v-tabs v-model="tab" centered fixed-tabs>
            <v-tabs-slider color="white"></v-tabs-slider>
            <v-tab> Active</v-tab>
            <v-tab> Closed </v-tab>
          </v-tabs>
        </v-col>

        <!-- Custom Headers -->
        <v-col cols="12" md="6" lg="4">
          <v-text-field
            label="Search"
            outlined
            dense
            append-icon="mdi-magnify"
            clearable
            hide-details="auto"
            v-model="filters.search"
            @input="searchTransporterContracts"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="1">
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                color="primary"
                v-on="on"
                v-bind="attrs"
                @click="showFilterDialog = true"
              >
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </template>

            <span v-if="Object.keys(filters).length == 0">Filters</span>

            <v-row v-else no-gutters>
              <v-col cols="12" class="font-weight-bold">Applied Filters</v-col>
              <v-col cols="12">
                <span
                  v-for="(value, key) in filters"
                  :key="key"
                  class="text-capitalize"
                  >{{ key.replace("_", " ") }}
                  <br />
                </span>
              </v-col>
            </v-row>
          </v-tooltip>
        </v-col>

        <v-col cols="12" md="1" lg="3"></v-col>

        <v-col cols="12" md="4">
          <GeneralColumnToShow
            :allHeaders="headers"
            :selectedHeaders="headerSelected"
            itemText="headerName"
            itemValue="field"
            @headersChanged="headersChanged"
          />
        </v-col>

        <v-col cols="12">
          <AgGridVue
            style="width: 100%; height: calc(100vh - 388px)"
            :class="
              $vuetify.theme.dark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'
            "
            id="myGrid"
            :columnDefs="headersForTable"
            :context="context"
            @grid-ready="onGridReady"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            :suppressDragLeaveHidesColumns="true"
            :rowData="transporterContractList"
          ></AgGridVue>
        </v-col>

        <v-col v-if="totalItems > 0" cols="12" class="d-flex justify-end">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-col>
      </v-row>

      <DetailsParent :resizable="true" v-model="displayDrawer">
        <template #details>
          <TransporterContractDetails
            :permissions="permissions"
            @submitResp="showConfirmationDialog"
            @refreshList="getTransporterContractList"
            :contractId="selectedContractId"
          />
        </template>
      </DetailsParent>

      <AdminTransporterContractFilters
        v-model="showFilterDialog"
        :bindingObject="filters"
        @resetFilters="resetFilters"
        @applyFilters="itemsPerPageChanged(10)"
      />
    </template>

    <template #dialogs>
      <RenewContractDialog
        v-model="renewDialog"
        :formDetails="renewObject"
        :formErrors="renewObjectErrors"
        @submitRemarks="renewContract"
        @removeFieldError="renewObjectErrors = {}"
      />
    </template>

    <template #actions>
      <v-btn
        v-if="permissions && permissions.add"
        @click="addContract()"
        color="primary"
        depressed
      >
        <v-icon>mdi-plus</v-icon>
        <span>Add Tariff</span>
      </v-btn>
    </template>
  </GeneralListComponent>
</template>

<script>
import GeneralColumnToShow from "@/components/General/ColumnToShow.vue";
import GeneralListComponent from "@/components/General/ListComponent.vue";
import RemarksDialog from "@/components/ModuleBased/dialogs/RemarksDialog.vue";
import { AgGridVue } from "ag-grid-vue";
import Pagination from "@/components/General/Pagination.vue";
import AdminTransporterContractButton from "@/components/AgGridComponents/AdminTransporterContractButton.vue";
import RenewContractDialog from "@/components/ModuleBased/dialogs/RenewContractDialog.vue";
import CustomHeader from "@/components/AgGridComponents/General/CustomHeader.vue";
import TransporterContractDetails from "@/components/ModuleBased/details/TransporterContractDetails.vue";
import DetailsParent from "@/components/General/DetailsParent.vue";
import AdminTransporterContractFilters from "@/components/ModuleBased/dialogs/filters/AdminTransporterContractFilters.vue";
import RejectionRemarksDialog from "@/components/ModuleBased/dialogs/RejectionRemarksDialog.vue";

import {
  isAuthenticated,
  hasRouteAccess,
  getPermissions,
} from "@/utils/functions";

export default {
  components: {
    GeneralListComponent,
    GeneralColumnToShow,
    RemarksDialog,
    AgGridVue,
    Pagination,
    AdminTransporterContractButton,
    agColumnHeader: CustomHeader,
    TransporterContractDetails,
    DetailsParent,
    AdminTransporterContractFilters,
    RenewContractDialog,
    RejectionRemarksDialog,
  },
  data() {
    return {
      renewDialog: false,
      renewObject: {},
      renewObjectErrors: {},
      showCloseRemarksForm: false,

      sorting: {},
      headers: [
        {
          headerName: "Tariff ID",
          field: "contract_id",
          sortable: true,
          minWidth: 100,
        },
        {
          headerName: "Transporter",
          field: "transporter_name",
          sortable: true,
          minWidth: 250,
        },
        {
          headerName: "Lane",
          field: "transporter_contract_lane_title",
          tooltipField: "transporter_contract_lane_title",
          sortable: true,
          minWidth: 800,
        },
        {
          headerName: "Start Date",
          field: "start_date",
          sortable: true,
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value);
          },
        },
        {
          headerName: "End Date",
          field: "end_date",
          sortable: true,
          valueFormatter: (params) => {
            return this.$globals.formatDate(params.value);
          },
        },
      ],
      transporterContractList: [],

      // details vars
      selectedContractId: null,
      displayDrawer: false,
      permissions: null,

      // pagination vars
      pageNo: 1,
      itemsPerPage: 10,
      totalItems: 100,

      // filter vars
      filters: {},
      showFilterDialog: false,

      headerSelected: [],
      tab: null,

      contractDetails: {},
      // update Contract Details
      updatingContract: {},
      showRemarksForm: false,

      // ag-grid vars
      gridApi: null,
      gridColumnApi: null,
      defaultColDef: {
        width: 150,
        resizable: true,
        flex: 1,
      },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
      },
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    headersForTable() {
      return [
        ...this.headerSelected,
        {
          headerName: "Actions",
          field: "actions",
          width: 300,
          cellRendererFramework: "AdminTransporterContractButton",
        },
      ];
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
  },
  watch: {
    tab() {
      this.getTransporterContractList({ offset: 0, limit: this.itemsPerPage });
      this.displayDrawer = false;
    },
  },
  methods: {
    showConfirmationDialog() {
      this.showCloseRemarksForm = true;
    },
    closeRemarksSubmit() {
      this.$bus.$emit("showLoader", true);
      let payload = {
        id: this.selectedContractId,
        close_remarks: this.contractDetails.close_remarks,
      };

      this.$api.transporterContract
        .closeTransporterContract(payload)
        .then((res) => {
          this.getTransporterContractList();
          this.$bus.$emit("showToastMessage", {
            message: "Tariff closed successfully!",
            color: "success",
          });
          this.showRemarksForm = false;
          this.$bus.$emit("showLoader", false);
          this.showCloseRemarksForm = false;
          this.selectedContractId = false;
          this.displayDrawer = false;
          delete this.contractDetails.close_remarks;
        })
        .catch((err) => {
          this.formErrors = err.data;
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't update Tariff status!",
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },
    resetFilters() {
      this.filters = {};
      this.itemsPerPageChanged(10);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },
    searchTransporterContracts() {
      this.pageNo = 1;
      this.getTransporterContractList();
    },
    openRemarksForm(id, status) {
      this.updatingContract = {
        id: id,
        status: status,
      };
      this.showRemarksForm = true;
    },
    updateContract() {
      this.$bus.$emit("showLoader", true);
      let payload = {
        id: this.updatingContract.id,
        data: this.updatingContract,
      };
      this.$api.transporterContract
        .partiallyUpdateTransporterContract(payload)
        .then((res) => {
          this.getTransporterContractList();
          this.$bus.$emit("showToastMessage", {
            message: "Tariff status updated successfully!",
            color: "success",
          });
          this.showRemarksForm = false;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showToastMessage", {
            message: "Couldn't update Tariff status!",
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
    headersChanged(value) {
      this.headerSelected = value;
      this.resizeGrid();
    },
    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getTransporterContractList();
    },
    prevPage() {
      this.pageNo--;
      this.getTransporterContractList();
    },
    nextPage() {
      this.pageNo++;
      this.getTransporterContractList();
    },
    applyGridSort(key, type) {
      if (this.filters && !this.filters.ordering) {
        this.filters.ordering = [];
      }

      if (type == null) {
        delete this.sorting[key];
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(`-${key}`),
          1
        );
      } else if (type == "asc") {
        this.filters.ordering.push(key);
      } else if (type == "desc") {
        this.filters.ordering.splice(
          this.filters.ordering.indexOf(key),
          1,
          `-${key}`
        );
      }
      if (this.filters.ordering.length == 0) {
        delete this.filters.ordering;
      }
      this.getTransporterContractList();
    },
    getTransporterContractList(params = {}) {
      this.$bus.$emit("showLoader", true);
      params = {
        offset: this.offset,
        limit: this.itemsPerPage,
        status: this.tab == 0 ? "Active" : "Closed",
        ...params,
        ...this.filters,
      };
      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }
      this.$api.transporterContract
        .getTransporterContractList(params)
        .then((res) => {
          this.transporterContractList = res.data;
          this.totalItems = res.count;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    addContract(id) {
      const path = {
        path: "/admin/contracts/transporter/add",
        query: {
          active_tab: this.tab,
        },
      };

      if (id) {
        path.query.id = id;
      }
      this.$router.push(path);
    },
    editContract(id) {
      const path = {
        path: `/admin/contracts/transporter/edit/${id}`,
        query: {
          active_tab: this.tab,
        },
      };
      this.$router.push(path);
    },
    viewContract(id) {
      this.selectedContractId = id;
      this.displayDrawer = true;
    },
    deleteSpotRate(user) {
      this.$bus.$emit("showToastMessage", {
        message: "This is a test",
      });
    },
    checkQuery() {
      if ("active_tab" in this.$route.query) {
        this.tab = parseInt(this.$route.query.active_tab);
      }
    },
    showRenewDialog(id) {
      this.selectedContractId = id;
      this.renewDialog = true;
    },
    renewContract() {
      const params = {
        id: this.selectedContractId,
        data: this.renewObject,
      };
      this.$api.transporterContract
        .renewContract(params)
        .then((res) => {
          this.renewDialog = false;
          this.getTransporterContractList();
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Tariff Renewed Successfully!",
            color: "success",
          });
          this.renewObject.start_date = null;
          this.renewObject.end_date = null;
        })
        .catch((err) => {
          console.error(err);
          this.renewObjectErrors = err.data;
          this.$bus.$emit("showToastMessage", {
            message: `${
              err.data && err.data.non_field_errors
                ? err.data.non_field_errors.join()
                : "Something went wrong!"
            }`,
            color: "error",
          });
          this.$bus.$emit("showLoader", false);
        });
    },
  },
  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("contract");
      if (!permissions || !permissions.view) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/${
            localStorage.getItem("user_type")
              ? localStorage.getItem("user_type").toLowerCase()
              : "error"
          }`
        );
      } else {
        this.permissions = { ...permissions };
        this.checkQuery();
      }
    }
  },
};
</script>